import React, { useState } from "react";
import { Check, CalendarClock, ClipboardList, ListTodo, CalendarRange, CheckCircle2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const KIND = {
  plan: { dot: "bg-violet-500", soft: "bg-violet-50 text-violet-900 border-violet-200", icon: CalendarRange, label: "Plan" },
  visit: { dot: "bg-sky-500", soft: "bg-sky-50 text-sky-900 border-sky-200", icon: ClipboardList, label: "Visit" },
  followup: { dot: "bg-orange-500", soft: "bg-orange-50 text-orange-900 border-orange-200", icon: CalendarClock, label: "Follow-up" },
  task: { dot: "bg-emerald-600", soft: "bg-emerald-50 text-emerald-900 border-emerald-200", icon: ListTodo, label: "Task" },
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };

function Reschedule({ e, onChanged }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(e.date);
  const move = async (to) => {
    try {
      await api.patch(`/plans/${e.id}/move`, { planned_date: to });
      toast.success(`Moved to ${new Date(`${to}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`);
      setOpen(false); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reschedule"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="p-0.5 rounded hover:bg-white/70" title="Reschedule" data-testid={`agenda-reschedule-${e.id}`} onClick={(ev) => ev.stopPropagation()}><CalendarDays className="w-3 h-3" /></button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-3 space-y-2" data-testid={`agenda-reschedule-pop-${e.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">Reschedule</div>
        <div className="flex gap-1.5">
          <button className="flex-1 text-xs px-2 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" onClick={() => move(addDays(e.date, 1))} data-testid={`agenda-resched-tomorrow-${e.id}`}>Tomorrow</button>
          <button className="flex-1 text-xs px-2 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" onClick={() => move(addDays(e.date, 7))} data-testid={`agenda-resched-nextweek-${e.id}`}>Next week</button>
        </div>
        <div className="flex gap-1.5">
          <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} className="flex-1 text-xs px-2 py-1 rounded-md border border-slate-200 bg-white" data-testid={`agenda-resched-date-${e.id}`} />
          <button className="text-xs px-2 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40" disabled={!date || date === e.date} onClick={() => move(date)} data-testid={`agenda-resched-apply-${e.id}`}>Move</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AgendaItem({ e, canAct, onChanged }) {
  const k = KIND[e.kind] || KIND.task;
  const Icon = k.icon;
  const done = e.status === "done" || e.status === "completed";
  const markDone = async (ev) => {
    ev.stopPropagation();
    try {
      if (e.kind === "plan") {
        const { data } = await api.patch(`/plans/${e.id}/status`, { status: "done", log_visit: true });
        toast.success(data.visit ? `Done — visit logged at ${data.visit.facility_name}` : "Marked done");
      } else {
        await api.patch(`/followups/${e.id}`, { status: "done" });
        toast.success("Follow-up completed");
      }
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };
  const actionable = canAct && !done && (e.kind === "plan" || e.kind === "followup");
  return (
    <li className={`group flex items-start gap-1.5 rounded-md border px-2 py-1 text-[11px] leading-tight ${k.soft} ${done ? "opacity-60" : ""}`} data-testid={`sidebar-agenda-item-${e.kind}-${e.id}`} title={`${k.label}${e.owner_name ? ` · ${e.owner_name}` : ""}`}>
      {done ? <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" /> : <Icon className="w-3 h-3 mt-0.5 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-medium ${done ? "line-through" : ""}`}>{e.title}</span>
        {e.facility_name && <span className="block truncate opacity-75">{e.facility_name}</span>}
      </span>
      {actionable && (
        <span className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100">
          <button className="p-0.5 rounded hover:bg-white/70" title="Mark done" onClick={markDone} data-testid={`agenda-done-${e.id}`}><Check className="w-3 h-3" /></button>
          {e.kind === "plan" && <Reschedule e={e} onChanged={onChanged} />}
        </span>
      )}
    </li>
  );
}
